import { normalizeSubscriptionPlan } from '../constants/uploadLimits';

/**
 * @param {import('firebase/firestore').DocumentData | null | undefined} billing
 * @param {{ isAdmin?: boolean, now?: number }} [options]
 */
export function resolveStudioTrialExpiredNotice(billing, { isAdmin = false, now = Date.now() } = {}) {
  if (isAdmin || !billing) {
    return { active: false, expiredAt: null };
  }

  const override = billing.manualPlanOverride;
  const expiresAt =
    override && typeof override.expiresAt === 'number' && Number.isFinite(override.expiresAt)
      ? override.expiresAt
      : null;
  // Legacy Studio trials are stored as plan "studio". normalizeSubscriptionPlan folds
  // that id into capture_pack, so the expired-trial notice has to read the raw id.
  const rawOverridePlan = String(override?.plan ?? '')
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '_');
  const overridePlan =
    override?.plan != null ? normalizeSubscriptionPlan(override.plan) : null;
  const overrideExpired = !!(overridePlan && expiresAt != null && now >= expiresAt);

  const subscriptionPlan = normalizeSubscriptionPlan(billing?.subscription?.plan || 'free');
  const active =
    overrideExpired
    && rawOverridePlan === 'studio'
    && subscriptionPlan === 'free';

  return {
    active,
    expiredAt: active ? expiresAt : null,
  };
}

/** @param {string} uid @param {number|null|undefined} expiredAt */
export function studioTrialExpiredDismissStorageKey(uid, expiredAt) {
  return `starling_studio_trial_expired_dismissed_${uid}_${expiredAt ?? 'unknown'}`;
}

/** @param {string} uid @param {number|null|undefined} expiredAt */
export function isStudioTrialExpiredDismissed(uid, expiredAt) {
  if (!uid || expiredAt == null) return false;
  try {
    return localStorage.getItem(studioTrialExpiredDismissStorageKey(uid, expiredAt)) === '1';
  } catch {
    return false;
  }
}

/** @param {string} uid @param {number|null|undefined} expiredAt */
export function markStudioTrialExpiredDismissed(uid, expiredAt) {
  if (!uid || expiredAt == null) return;
  try {
    localStorage.setItem(studioTrialExpiredDismissStorageKey(uid, expiredAt), '1');
  } catch {
    /* ignore */
  }
}

export function isStudioTrialExpiredPreviewEnabled() {
  if (typeof window === 'undefined') return false;
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get('previewStudioTrialExpired') === '1') return true;
    return localStorage.getItem('starling_preview_studio_trial_expired') === '1';
  } catch {
    return false;
  }
}
